@if (showLoading$ | async) {
  <app-step-slides-loading></app-step-slides-loading>
} @else {
<div class="max-w-3xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
    <!-- Title and Description -->
    <div class="text-center space-y-2 mb-4">
      <h2 class="text-2xl font-bold text-gray-800">Customize Your Lesson Outline</h2>
      <p class="text-gray-500">Review and adjust the structure that will guide your slides.</p>
    </div>

    <!-- Sticky Header -->
    <div class="sticky top-[88px] md:top-20 z-20 bg-[#FAFAFA] pb-2 pt-1">
      @if (timeInfo$ | async; as timeInfo) {
        <div
          class="bg-white border border-gray-200 p-4 rounded-xl flex items-center justify-between shadow-sm"
        >
          <div>
            <h2 class="text-lg font-bold text-gray-800">Lesson Outline</h2>
            <p class="text-xs text-gray-500">This structure drives your slides.</p>
          </div>
          <div
            class="px-3 py-1 bg-gray-50 rounded-lg border font-mono font-bold text-sm flex items-center gap-1"
            [class]="timeInfo.color"
          >
            <lucide-icon [img]="icons.Clock" class="w-3.5 h-3.5"></lucide-icon>
            {{ timeInfo.total }}/{{ timeInfo.duration }}m
          </div>
        </div>
      }
    </div>

    <!-- Timeline Blocks -->
    @if (planStructure$ | async; as timeline) {
      <div class="space-y-4 pb-20">
        @for (block of timeline; track block.id; let first = $first; let last = $last) {
          <div class="bg-white p-4 rounded-xl shadow-sm border border-gray-100 relative group">
            <!-- Block Actions -->
            <div class="absolute top-2 right-2 flex items-center gap-1">
              <!-- Move Up -->
              <button
                (click)="moveBlockUp(block.id)"
                [disabled]="first"
                class="p-1.5 text-gray-300 hover:text-gray-600 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                [class.hover:text-gray-600]="!first"
              >
                <lucide-icon [img]="icons.ChevronUp" class="w-4 h-4"></lucide-icon>
              </button>
              <!-- Move Down -->
              <button
                (click)="moveBlockDown(block.id)"
                [disabled]="last"
                class="p-1.5 text-gray-300 hover:text-gray-600 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                [class.hover:text-gray-600]="!last"
              >
                <lucide-icon [img]="icons.ChevronDown" class="w-4 h-4"></lucide-icon>
              </button>
              <!-- Delete -->
              <button
                (click)="deleteBlock(block.id)"
                class="p-1.5 text-gray-300 hover:text-red-500 transition-colors cursor-pointer"
              >
                <lucide-icon [img]="icons.X" class="w-4 h-4"></lucide-icon>
              </button>
            </div>

            <div class="flex flex-col md:flex-row gap-4">
              <!-- Duration and Phase (Mobile) -->
              <div class="flex items-center gap-3 md:block md:w-24">
                <div class="w-16">
                  <label class="text-[10px] font-bold text-gray-400 uppercase block mb-1"
                    >Mins</label
                  >
                  <input
                    type="number"
                    class="w-full font-mono font-bold text-gray-800 bg-gray-50 border border-gray-200 rounded p-2 text-center focus:ring-2 focus:ring-brand/20 outline-none"
                    [value]="block.duration"
                    (input)="updateBlock(block.id, 'duration', $event)"
                  />
                </div>
                <div class="flex-1 md:hidden pt-4">
                  <textarea
                    appAutoResizeTextarea
                    class="w-full font-bold text-lg text-gray-800 bg-transparent border-b border-gray-100 focus:border-brand outline-none resize-none overflow-hidden"
                    [value]="block.phase"
                    (input)="updateBlock(block.id, 'phase', $event)"
                  ></textarea>
                </div>
              </div>

              <!-- Content -->
              <div class="flex-1 space-y-3">
                <!-- Phase (Desktop) -->
                <div class="hidden md:block">
                  <textarea
                    appAutoResizeTextarea
                    class="w-full font-bold text-lg text-gray-800 bg-transparent border-b border-transparent focus:border-brand-light hover:border-gray-100 outline-none transition-all resize-none overflow-hidden"
                    [value]="block.phase"
                    (input)="updateBlock(block.id, 'phase', $event)"
                  ></textarea>
                </div>

                <!-- Activity Name -->
                <div>
                  <label class="text-[10px] font-bold text-gray-400 uppercase block mb-1"
                    >Activity Name</label
                  >
                  <textarea
                    appAutoResizeTextarea
                    class="w-full p-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none resize-none overflow-hidden"
                    [value]="block.activity"
                    (input)="updateBlock(block.id, 'activity', $event)"
                  ></textarea>
                </div>

                <!-- Details -->
                <div>
                  <label class="text-[10px] font-bold text-gray-400 uppercase block mb-1"
                    >Details (For Slide Visuals)</label
                  >
                  <textarea
                    appAutoResizeTextarea
                    class="details-textarea w-full p-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none resize-none overflow-hidden"
                    [value]="block.description"
                    (input)="updateBlock(block.id, 'description', $event)"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
        }

        <!-- Add Block Button -->
        <button
          (click)="addBlock()"
          class="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 font-medium hover:border-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-all flex items-center justify-center gap-2 cursor-pointer"
        >
          <lucide-icon [img]="icons.Plus" class="w-5 h-5"></lucide-icon>
          Add Block
        </button>

        <!-- Navigation Buttons -->
        <div
          class="fixed bottom-0 left-0 w-full bg-white border-t border-gray-100 p-4 flex gap-3 z-40 md:static md:bg-transparent md:border-0 md:p-0"
        >
          <button
            (click)="onBack()"
            class="flex-1 py-3 text-gray-500 font-medium bg-gray-100 rounded-xl cursor-pointer hover:bg-gray-200 transition-colors"
          >
            Back
          </button>
          <button
            (click)="onCreateSlides()"
            class="flex-[2] py-3 text-white font-bold rounded-xl shadow-lg flex items-center justify-center gap-2 cursor-pointer"
            [style.backgroundColor]="themeColor"
          >
            <lucide-icon [img]="icons.Sparkles" class="w-4.5 h-4.5"></lucide-icon>
            Create Slides
          </button>
        </div>
      </div>
    }
</div>
}
